/**
 * Resolve the window/document that actually OWNS a node or event (#761).
 *
 * When a viewer is detached into its own browser window, its subtree still lives
 * in the main window's Preact tree but its DOM nodes belong to the detached
 * document. Code that reaches for the global `window` / `document` — drag
 * handlers listening for `mousemove`/`mouseup`, keyboard shortcuts, viewport
 * clamping, fullscreen — would then act on the wrong window: drags never
 * complete and shortcuts are swallowed by the main page.
 *
 * These resolvers return the owning window/document, falling back to the globals.
 * When nothing is detached they resolve to exactly the globals, so the docked
 * behaviour is unchanged.
 */

type Resolvable = Event | Node | null | undefined;

function documentOfNode(node: Node): Document {
    // A Document's own `ownerDocument` is null — it IS the document.
    return (node.nodeType === 9 ? node as Document : node.ownerDocument) ?? document;
}

/** The `Document` that owns `target`; the global `document` when unresolvable. */
export function ownerDocumentOf(target: Resolvable): Document {
    if (!target) return document;

    if (target instanceof Event || 'currentTarget' in target) {
        const evt = target as Event & { view?: Window | null };
        // A UIEvent carries its window directly; prefer it.
        const view = evt.view;
        if (view?.document) return view.document;
        const node = (evt.currentTarget ?? evt.target) as Node | null;
        return node ? documentOfNode(node) : document;
    }

    return documentOfNode(target as Node);
}

/**
 * The `ResizeObserver` constructor of the window that owns `target` (#761 review).
 *
 * This is not a nicety: an observer built with window A's constructor delivers
 * **nothing** for elements living in window B's document. Measured in a browser:
 *
 * ```
 * new opener.ResizeObserver(cb).observe(detachedEl)  // cb never fires
 * new window.ResizeObserver(cb).observe(detachedEl)  // cb fires
 * ```
 *
 * So an observer must be built from the owning window, and rebuilt whenever the
 * observed element changes document — which a detach does, because it remounts.
 */
export function observerCtorFor(target: Resolvable): typeof ResizeObserver {
    return (ownerWindowOf(target) as Window & { ResizeObserver: typeof ResizeObserver })
        .ResizeObserver ?? ResizeObserver;
}

/**
 * `requestAnimationFrame` bound to the window that owns `target`.
 *
 * A background window's frames are throttled by the browser, so a paint loop
 * scheduled on the main window stalls while the user works in the detached one.
 */
export function rafFor(target: Resolvable): (cb: FrameRequestCallback) => number {
    const win = ownerWindowOf(target);
    return (cb: FrameRequestCallback) => win.requestAnimationFrame(cb);
}

/** `cancelAnimationFrame` bound to the window that owns `target`. */
export function cancelRafFor(target: Resolvable): (handle: number) => void {
    const win = ownerWindowOf(target);
    return (handle: number) => win.cancelAnimationFrame(handle);
}

/** The `Window` that owns `target`; the global `window` when unresolvable. */
export function ownerWindowOf(target: Resolvable): Window {
    if (!target) return window;

    if (target instanceof Event || 'currentTarget' in target) {
        const evt = target as Event & { view?: Window | null };
        if (evt.view) return evt.view;
    }

    return ownerDocumentOf(target).defaultView ?? window;
}
